import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach } from '@/features/legends/editor/collections'
import type { SymbolContext } from '@/features/legends/editor/entities'
import { validateElements } from '@/utils/validx-util'
import type { FilterEditorViewModelOptions } from './FilterEditorViewModelBase'
import {
  type AnyFilterEditor,
  createFilter,
  hydrateFilter,
} from './filter-registry'
import type { FilterScope } from './filter-scope'
import { groupFilterTypes } from './filter-types'
import type { FilterType, Rule } from './rules'

export interface FiltersBuilderViewModelOptions {
  dependencies: LegendPartDependencies
  scope?: readonly FilterScope[]
  /**
   * The builder or entity the filters belong to. Decides which symbols they
   * can reference.
   */
  context: SymbolContext
}

/**
 * A list of filters that all must pass.
 */
export class FiltersBuilderViewModel extends LegendPartViewModelBase {
  private readonly _scope: readonly FilterScope[]
  private readonly _context: SymbolContext
  private readonly _schema: IValidationSchema<FiltersBuilderViewModel> = {
    filters: [validateElements('There are invalid filters.')],
  }

  readonly filters = observable.array<AnyFilterEditor>([], { deep: false })

  /**
   * Keeps an accordion holding the builder open across renders.
   */
  @observable accessor isExpanded = false

  readonly validation = validationContext(this, this._schema)

  constructor({
    dependencies,
    scope,
    context,
  }: FiltersBuilderViewModelOptions) {
    super(dependencies)
    this._scope = scope ?? []
    this._context = context
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.filters))
  }

  /**
   * The number of filters.
   */
  @computed
  get length() {
    return this.filters.length
  }

  /**
   * Whether there are no filters.
   */
  @computed
  get isEmpty() {
    return this.length === 0
  }

  /**
   * The filter types that can be added in the builder's scope.
   */
  @computed
  get availableFilterTypes() {
    return groupFilterTypes(this._scope)
  }

  @computed
  private get _json(): Rule[] {
    return this.filters.map((x) => x.toJSON())
  }

  private get _filterOptions(): FilterEditorViewModelOptions {
    return {
      dependencies: this.dependencies,
      scope: this._scope,
      context: this._context,
    }
  }

  /**
   * Sets the filters from the legend document.
   */
  @action
  hydrate(rules: Rule[] | undefined) {
    this.filters.replace(
      (rules ?? []).map((rule) => hydrateFilter(rule, this._filterOptions)),
    )
  }

  /**
   * Adds an empty filter of the type.
   */
  @action.bound
  addFilter(type: FilterType) {
    this.filters.push(createFilter(type, this._filterOptions))
  }

  /**
   * Removes the filter.
   */
  @action.bound
  removeFilter(filter: AnyFilterEditor) {
    this.filters.remove(filter)
  }

  /**
   * Opens or closes the accordion holding the builder.
   */
  @action.bound
  setExpanded(isExpanded: boolean) {
    this.isExpanded = isExpanded
  }

  /**
   * Shows the analyzer's errors on the filters.
   */
  receiveErrors(descriptor: ErrorDescriptor | null | undefined) {
    addGenericErrorForProperty(
      this,
      descriptor,
      'filters',
      'There are invalid conditions',
    )
    receiveErrors(this.filters, descriptor?.inner)
  }

  /**
   * The filters as the legend document has them.
   */
  toJSON(): Rule[] {
    return this._json
  }
}
